<template>
  <tf-table-parent-row
    @mouseover.native="isHovering = true"
    @mouseout.native="isHovering = false"
  >
    <tf-table-td data-test-product-type-name>
      <router-link
        class="cta"
        :tag="rowData.state === 'completed' ? 'a' : 'span'"
        :to="rowData.state === 'completed'
          ? {
            name: 'CoverageType',
            params: { productTypeId, coverageType },
          }
          : {}"
      >
        <tf-button
          type="primary"
          size="text"
          :disabled="rowData.state !== 'completed'"
        >
          {{ productTypeName }}
        </tf-button>
      </router-link>
      <bp-tooltip
        v-if="rowData.state !== 'completed'"
        placement="right"
      >
        <icon
          slot="reference"
          class="clock-icon"
          :name="iconName"
        />
        <template v-if="rowData.state === 'not_started'">
          To go to market or collect a renewal,
          <router-link
            class="cta"
            :to="{
              name: 'NewLinesOfCoverage',
              params: { productTypeId },
            }"
          >
            finish adding this current coverage
          </router-link>.
          To remove this coverage, <tf-cta-email-support no-branding />.
        </template>
        <template v-else>
          Current coverage setup in progress.
        </template>
      </bp-tooltip>
    </tf-table-td>
    <tf-table-td>
      {{ rowData.carrier_name }}
    </tf-table-td>
    <commissions-cell :commissions="rowData.commissions" />
    <tf-table-td>
      <tf-inline-form-field
        ref="inlineFormField"
        v-bind="{
          isEditing,
          isHovering,
          isSaving: false,
          showBtnDelete: false,
          showBtnActions: false,
          showExtension: false,
          prop: dateProp,
          value: $TF.DatesUtils.formatSimpleDate(rowData[dateProp], 'MM/DD/YY')
        }"
        @edit="onEdit"
      >
        <tf-datepicker
          ref="datepicker"
          v-bind="{
            showCalendarOnFocus: true,
            useUtc: true,
            value: rowData[dateProp],
          }"
          data-test-date
          fixed-position="bottom-right"
          @input="saveDate"
          @closed="isEditing = false"
        />
      </tf-inline-form-field>
    </tf-table-td>
    <template v-if="isCurrentCoverage">
      <tf-table-td>
        <router-link
          data-test-cta1
          class="cta"
          v-bind="{ ...ctaProps }"
        >
          <tf-button
            type="primary"
            size="text"
            :disabled="ctaIsDisabled"
          >
            {{
              rowData.state === 'not_started'
                ? 'Finish adding'
                : 'Collect renewal'
            }}
          </tf-button>
        </router-link>
      </tf-table-td>
      <tf-table-td>
        <router-link
          v-if="rowData.state !== 'completed'"
          data-test-cta2
          class="cta"
          :to="{
            name: 'AddClientRecordFiles',
            params: {
              categoryType: $TF.Product.isStopLoss(rowData)
                ? 'medical'
                : 'non-medical',
              documentId: rowData.document_id,
            },
          }"
        >
          <tf-button
            type="primary"
            size="text"
            @click="$TF.Analytics.trackEvent('Add files')"
            v-text="'Add files'"
          />
        </router-link>
        <!--
          We need an empty span here as the tf-table-td
          component has a default slot of &mdash;
        -->
        <span v-else />
      </tf-table-td>
    </template>
  </tf-table-parent-row>
</template>

<script>
  // vuex
  import {
    mapGetters,
  } from 'vuex';
  // services
  import ProductService from '@/services/product';
  import { putCoverageProp } from '@/services/coverages';

  /**
   * Coverage Row for current and upcoming coverages that relies on the store and API response data to render various table cells.
   *
   * @vuedoc
   * @exports Client/Tables/CoverageRow
   * @category Components
   */
  export default {
    name: 'CoverageRow',
    components: {
      CommissionsCell: () => import('@/components/Employer/Tables/CommissionsCell'),
    },
    props: {
      coverageType: {
        type: String,
        default: 'current',
        validator: (v) => ['current', 'upcoming'].includes(v),
      },
      rowData: {
        type: Object,
        required: true,
      },
    },
    data: () => ({
      classDescriptions: [],
      commissionsModalVisible: false,
      isEditing: false,
      isHovering: false,
    }),
    computed: {
      ...mapGetters(['activeEventsProductTypeIds']),
      /**
       * Disable the CTA if the product state is neither 'completed' nor 'not_started', otherwise, disable if the project product is being used in an existing event.
       *
       * @returns {boolean}
       */
      ctaIsDisabled() {
        return !['completed', 'not_started'].includes(this.rowData.state)
          || this.activeEventsProductTypeIds.includes(this.productTypeId);
      },
      /**
       * Cache the date prop to be used in template and also the API payload.
       *
       * @returns {boolean}
       */
      dateProp() {
        return this.coverageType === 'current'
          ? 'renewal_date'
          : 'effective_date';
      },
      /**
       * Sets the tooltip icon based on the CC state
       *
       * @returns {string}
       */
      iconName() {
        return this.rowData.state === 'not_started'
          ? 'exclamation-triangle'
          : 'regular/clock';
      },
      /**
       * Cache if the coverage type is current.
       *
       * @returns {boolean}
       */
      isCurrentCoverage() {
        return this.coverageType === 'current';
      },
      /**
       * Computed props for cta 1 router link
       *
       * @returns {object}
       */
      ctaProps() {
        const blank = {
          tag: 'div',
          to: {},
        };

        switch (this.rowData.state) {
        case 'not_started':
          return {
            tag: 'a',
            to: {
              name: 'NewLinesOfCoverage',
              params: { productTypeId: this.productTypeId },
            },
          };
        case 'completed':
          return this.ctaIsDisabled
            ? blank
            : {
              tag: 'a',
              to: {
                name: 'ClientEventsSelectCoverages',
                params: {
                  eventType: 'collect-renewals',
                  projectId: 'new',
                },
                query: {
                  productTypeId: this.productTypeId,
                },
              },
            };
        default:
          return blank;
        }
      },
      /**
       * Cache the product type ID.
       *
       * @returns {number}
       */
      productTypeId() {
        return this.rowData.product_type_id;
      },
      /**
       * Cache the product type Name.
       *
       * @returns {string}
       */
      productTypeName() {
        return this.rowData.product_type_name;
      },
    },
    methods: {
      /**
       * Toggle isEditing so focus on the datepicker on the next tick.
       */
      onEdit() {
        this.isEditing = true;

        this.$nextTick(() => {
          this.$refs.datepicker.$el.querySelector('input').focus();
        });
      },
      /**
       * PATCH the product renewal or effective date then mutate or emitted the data based on coverage type.
       *
       * @param {string} dateValue
       */
      async saveDate(dateValue) {
        const { dateProp: prop } = this;
        let value;
        let success = true;

        try {
          if (this.isCurrentCoverage) {
            ({ product: { [prop]: value } } = await ProductService.setProductValue({
              productId: this.rowData.product_id,
              prop,
              value: dateValue,
            }));
          } else {
            ({ coverage: { [prop]: value } } = await putCoverageProp(
              this.rowData.id,
              { [prop]: dateValue },
            ));
          }

          this.$emit('updateCoverageProp', { prop, value });
        } catch {
          success = false;
        } finally {
          this.$message({
            duration: 3000,
            message: `Updating the ${this.dateProp.replace('_', ' ')} for ${this.productTypeName}.`,
            showClose: true,
            type: success ? 'success' : 'error',
          });
        }
      },
    },
  };
</script>

<style scoped lang="scss">
  .cta {
    display: inline-block;
  }

  .commission-plans {
    display: inline-flex;
  }

  .class-description {
    h4 {
      margin-bottom: 0;
    }

    p {
      margin-top: 0;
    }

    &:not(:last-child) {
      margin-bottom: 10px;
    }
  }

  .clock-icon {
    color: $tf-orange;
    margin-left: 12px;
    transform: translateY(2px);
  }

  ::v-deep {
    .tf-datepicker input {
      max-width: 121px;
      height: 32px;
    }

    .tf-popper.tf-tooltip {
      max-width: 225px;

      a {
        font-size: 12px;
      }
    }
  }
</style>
